import { baseApi, resourceListTag, resourceScopeTag, resourceTag } from '@krp/client-runtime'

import { envelope } from '../../../shared/api/envelope'
import { AQSI_TAG_TYPES, AQSI_TAGS } from '../../../shared/api/tags'
import type { AqsiDiscrepancies, AqsiIncidentPage, AqsiRecoveryProof } from '../model/types'
import { decodeDiscrepancies, decodeIncidentPage, decodeRecoveryProof, INCIDENT_KINDS } from './contracts'
import { AQSI_INCIDENT_URLS } from './urls'

/** Страница журнала инцидентов: предел службы 200, по умолчанию 50. */
export const INCIDENTS_PAGE_SIZE = 50

/** Пределы журнала расхождений (служба: 1…200). */
export const DISCREPANCY_LIMITS: readonly number[] = [50, 100, 200]
export const DEFAULT_DISCREPANCY_LIMIT = 50

export interface IncidentsArgs {
  resourceId: string
  /** Ключ кассы из ответа службы (ключ кэша). */
  registerKey: string
  /** Касса кабинета, чей журнал читается (RP-C9). */
  deviceId: number
  /** Курсор страницы; `null` — первая страница (новый снимок на сервере). */
  cursor: string | null
}

export interface RecoveryProofArgs {
  resourceId: string
  saleId: string
}

export interface DiscrepanciesArgs {
  resourceId: string
  limit: number
}

/**
 * Журналы C4b в ОБЩЕМ `baseApi` — только чтения: ни кабинета, ни продвижения
 * состояния служба при них не делает. rid — в аргументах (ключ кэша) и тегах,
 * `resourceScopeTag(rid)` у каждого: сброс AQSI-A не перечитывает AQSI-B.
 */
export const incidentsApi = baseApi.enhanceEndpoints({ addTagTypes: AQSI_TAG_TYPES }).injectEndpoints({
  endpoints: (build) => ({
    aqsiSaleIncidents: build.query<AqsiIncidentPage, IncidentsArgs>({
      query: ({ resourceId, deviceId, cursor }) => ({
        url: AQSI_INCIDENT_URLS.incidents(resourceId),
        params: { deviceId, pageSize: INCIDENTS_PAGE_SIZE, cursor: cursor ?? undefined },
        decode: envelope(INCIDENT_KINDS.incidents, resourceId, decodeIncidentPage),
      }),
      providesTags: (_result, _error, { resourceId, registerKey }) => [
        resourceTag(AQSI_TAGS.incidents, resourceId, registerKey),
        resourceListTag(AQSI_TAGS.incidents, resourceId),
        resourceScopeTag(resourceId),
      ],
    }),
    aqsiRecoveryProof: build.query<AqsiRecoveryProof, RecoveryProofArgs>({
      query: ({ resourceId, saleId }) => ({
        url: AQSI_INCIDENT_URLS.recoveryProof(resourceId, saleId),
        decode: envelope(INCIDENT_KINDS.recoveryProof, resourceId, (raw) => decodeRecoveryProof(raw, saleId)),
      }),
      providesTags: (_result, _error, { resourceId, saleId }) => [
        resourceTag(AQSI_TAGS.recoveryProof, resourceId, saleId),
        resourceScopeTag(resourceId),
      ],
    }),
    aqsiReceiptDiscrepancies: build.query<AqsiDiscrepancies, DiscrepanciesArgs>({
      query: ({ resourceId, limit }) => ({
        url: AQSI_INCIDENT_URLS.discrepancies(resourceId),
        params: { limit },
        decode: envelope(INCIDENT_KINDS.discrepancies, resourceId, decodeDiscrepancies),
      }),
      providesTags: (_result, _error, { resourceId }) => [
        resourceListTag(AQSI_TAGS.discrepancies, resourceId),
        resourceScopeTag(resourceId),
      ],
    }),
  }),
})

export const { aqsiSaleIncidents, aqsiRecoveryProof, aqsiReceiptDiscrepancies } = incidentsApi.endpoints
